/**
 * Low-level HTTP request primitive for wsr-admin (Option A).
 *
 * The transport is the **vendored fleet axios factory** (`./httpClientFactory`,
 * from `software-architect-ai/templates/api-axios-instance.ts`) configured via
 * `createApiClient`. The four interceptors (base+headers / bearer / csrf /
 * silent-refresh+error-normalize) are centralized there; this module supplies
 * wsr-admin's config and a THIN request-config wrapper for the extras the factory
 * does not emit. Interceptor changes come from RE-VENDORING the fleet template,
 * never from editing it locally.
 *
 * Two surfaces, strictly separated:
 *
 *  - saas-auth COOKIE surface (`/api/auth/**`, e.g. refresh / logout / csrf):
 *      base = `getBootstrap().authOrigin` (tenant AUTH, from same-origin
 *      `GET /api/bootstrap`); `credentials:'include'`; CSRF double-submit
 *      (header `X-CSRF-Token` + body `csrfToken`, cookie-echo mode) on
 *      state-mutating methods only. NO bearer token.
 *
 *  - wsr-api BEARER surface (every non-auth, non-public path):
 *      base = `apiBase()` (`'/api'`, same-origin; Caddy rewrites to `/wsr-api`);
 *      `Authorization: Bearer <token>`; NO cookies, NO CSRF — it is a stateless
 *      OAuth2 resource server (CSRF disabled, JWT-authenticated;
 *      HANDOFF-wsr-api-platform-foundation §4.2). The `/public/**` (and the
 *      OpenAPI/actuator) permitAll tier routes to the same same-origin base but
 *      carries NO bearer.
 *
 * We deliberately DO NOT port dink-admin's `X-Session-Mode` admin-mutation
 * header — it is a dink business concern (front-desk vs open audit attribution)
 * with no wsr-admin equivalent. Add one here only if wsr-admin later needs it.
 *
 * Every call carries `X-Request-ID`. A `401` on a bearer call triggers ONE
 * silent `POST /api/auth/refresh` and ONE retry — bounded, never looping (the
 * factory's single-in-flight refresh + `_retry` guard guarantee it).
 */
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { createApiClient, ApiError as FactoryApiError } from './httpClientFactory';
import { ApiError, statusToCode, type ErrorCode } from './errors';
import { generateRequestId } from '@/utils/uuid';
import { getBootstrap } from '@/config/bootstrap';
import { apiBase } from '@/config/platform';
import {
  getAccessToken,
  setAccessToken,
  getCsrfToken,
  setCsrfToken,
  signOut,
} from '@/auth/authStore';

/** saas-auth cookie + CSRF surface. */
export function isAuthSurface(path: string): boolean {
  return path.startsWith('/api/auth/');
}

/**
 * wsr-api permitAll (public) tier — HANDOFF-wsr-api-platform-foundation §4.2:
 * `/public/**`, `/actuator/health`, `/swagger-ui/**`, `/v3/api-docs/**`. These
 * route to the wsr-api base but carry NO bearer (and NO cookies / CSRF).
 */
export function isPublicSurface(path: string): boolean {
  return (
    path.startsWith('/public/') ||
    path === '/actuator/health' ||
    path.startsWith('/swagger-ui/') ||
    path.startsWith('/v3/api-docs')
  );
}

/**
 * The wsr-api BEARER surface: every path that is neither the saas-auth surface
 * nor a public (permitAll) path. wsr-api is greenfield and its exact final path
 * set (`/admin/**`, `/me/**`, …) is not confirmable from this clone, so we adopt
 * the dink-admin posture per the spec — "everything authenticated is bearer" —
 * which is exactly wsr-api's `SecurityConfig` rule ("everything else →
 * authenticated"). Bearer token, NO CSRF, NO cookies, the bounded
 * 401→refresh→retry.
 */
export function isBearerSurface(path: string): boolean {
  return !isAuthSurface(path) && !isPublicSurface(path);
}

/** Two-base routing: auth paths → tenant AUTH origin; everything else → `/api`. */
function baseUrlFor(path: string): string {
  if (isAuthSurface(path)) return getBootstrap().authOrigin;
  return apiBase();
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * The single shared axios instance, built from the vendored fleet factory with
 * wsr-admin's Option-A config:
 *  - resolveBaseUrl = the two-base `baseUrlFor` routing.
 *  - tokens = the in-memory `authStore` (the factory NEVER touches storage).
 *  - bearer.protected = `isBearerSurface` (every non-auth, non-public path).
 *  - csrf.protected = mutating `/api/auth/**` only; mode = `cookie-echo`
 *    (`X-CSRF-Token` header + `csrfToken` body echo, cookie-first with a
 *    `GET /api/auth/csrf` fallback). wsr-api surfaces get NO CSRF, NO cookies.
 *  - csrf.cookieName = the saas-auth double-submit cookie (`journix_csrf`, the
 *    shared platform CSRF cookie).
 *  - refresh.path = `/api/auth/refresh`; onSessionExpired = `signOut()`.
 *  - generateRequestId = the UUID generator (`X-Request-ID`).
 *  - getTenantContext OMITTED — tenancy is server-derived from the JWT (no
 *    `X-Tenant-ID`).
 */
const instance: AxiosInstance = createApiClient({
  resolveBaseUrl: baseUrlFor,
  tokens: {
    getAccess: getAccessToken,
    // The refresh body's `expiresAt` is an ISO string (saas-auth shape); the
    // factory types it loosely — forward it through to the store unchanged.
    setAccess: (token, expiresAt) =>
      setAccessToken(token, (expiresAt as string | null | undefined) ?? null),
    getCsrf: getCsrfToken,
    setCsrf: setCsrfToken,
  },
  bearer: { protected: isBearerSurface },
  csrf: {
    // CSRF double-submit applies ONLY to the saas-auth cookie surface, on
    // state-mutating methods. wsr-api never gets a CSRF header.
    protected: (path, method) => isAuthSurface(path) && MUTATING.has(method),
    mode: () => 'cookie-echo',
    cookieName: 'journix_csrf',
  },
  refresh: { path: '/api/auth/refresh' },
  onSessionExpired: signOut,
  generateRequestId,
});

/**
 * wsr-admin parity guard, registered HERE (not in the vendored factory) so the
 * factory stays canonical. Scope the silent 401→refresh→retry to the BEARER
 * surface only. The factory treats ANY 401 as refreshable; wsr-admin refreshes
 * only on the wsr-api bearer surface (the cookie surface — refresh / logout /
 * csrf — either succeeds or is terminally unauthenticated, and the refresh POST
 * must never recurse into another refresh). Pre-marking every NON-bearer request
 * as `_retry` makes the factory's `!_retry` gate skip them, so a 401 on the auth
 * surface (including the refresh POST and the CSRF GET themselves) — or on a
 * public path — is normalized, never refreshed.
 */
instance.interceptors.request.use((req) => {
  const r = req as { _retry?: boolean };
  if (!isBearerSurface(pathOf(req)) && r._retry === undefined) {
    r._retry = true; // auth-surface + public + internal calls never trigger a refresh
  }
  return req;
});

/** Path of an axios request config — bare path or the pathname of an absolute URL. */
function pathOf(req: { url?: string }): string {
  const u = req.url ?? '';
  if (u.startsWith('http')) {
    try {
      return new URL(u).pathname;
    } catch {
      return u;
    }
  }
  return u;
}

/** Test seam — clears the single-in-flight refresh latch (now per-instance). */
export function __resetClientState(): void {
  // The single-in-flight refresh latch lives inside the factory instance and is
  // self-clearing (it nulls in a `.finally`), so it is always idle between
  // awaited tests. This seam is retained for source/test compatibility.
}

export interface RequestOptions {
  json?: unknown;
  /**
   * A `multipart/form-data` body. When set, the body is sent verbatim and we
   * deliberately DO NOT set `Content-Type`, so the browser/axios stamps
   * `multipart/form-data` with the correct boundary. Mutually exclusive with
   * `json` (a `FormData` body is never JSON). The bearer treatment is unchanged
   * (bearer only, no CSRF, no cookies) — only the Content-Type handling differs.
   */
  form?: FormData;
  query?: Record<string, string | number | undefined>;
  headers?: Record<string, string>;
}

export async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  // The factory emits Accept + X-Request-ID; this wrapper adds the extras the
  // factory does not: caller headers and the conditional Content-Type (only with
  // a JSON body).
  const headers: Record<string, string> = { ...options.headers };
  if (options.json !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  // A `FormData` body must NOT carry an explicit Content-Type — the browser sets
  // `multipart/form-data; boundary=…` itself. A caller-spread `Content-Type`
  // (e.g. a stray JSON header) is dropped so the boundary is never clobbered.
  if (options.form !== undefined) {
    delete headers['Content-Type'];
  }

  const config: AxiosRequestConfig = {
    method,
    // Pass the bare path as the axios `url`; the factory's request interceptor
    // sets `baseURL` from `resolveBaseUrl(path)` so the surface predicates and
    // two-base routing operate on the clean path (not a query-laden URL).
    url: path,
    headers,
    // `credentials:'include'` ONLY on the saas-auth cookie surface. The factory
    // instance defaults `withCredentials: true` (correct for its internal
    // refresh/csrf auth-surface calls); we scope it per-request so wsr-api
    // bearer calls send NO cookies — the Option-A invariant.
    withCredentials: isAuthSurface(path),
  };
  if (options.json !== undefined) {
    // Only attach a body (and thus Content-Type) when a JSON payload is present.
    config.data = options.json;
  } else if (options.form !== undefined) {
    // A multipart body: pass the FormData through; axios/the browser sets the
    // `multipart/form-data` Content-Type + boundary (we left it unset above).
    config.data = options.form;
  }
  if (options.query) {
    config.params = options.query;
  }

  let data: T;
  try {
    const res = await instance.request<T>(config);
    data = res.status === 204 ? (undefined as T) : res.data;
  } catch (err) {
    throw withRequestContext(toApiError(err), method, path);
  }

  // Normalize an empty body to `null` (matching a fetch primitive, which returns
  // `null` for a zero-length non-204 body); 204 already returned above.
  return data === '' ? (null as T) : data;
}

/**
 * Convert a thrown factory `ApiError` into wsr-admin's `ApiError`, preserving the
 * code mapping: `NETWORK_ERROR` on transport failure (status 0),
 * `SESSION_EXPIRED` on a dead session, otherwise `statusToCode(status)` with the
 * server's diagnostic message extracted from the body.
 */
function toApiError(err: unknown): ApiError {
  if (err instanceof FactoryApiError) {
    if (err.code === 'NETWORK_ERROR') {
      return new ApiError('NETWORK_ERROR', 0, initFrom(err.requestId));
    }
    if (err.code === 'SESSION_EXPIRED') {
      return new ApiError('SESSION_EXPIRED', 401, initFrom(err.requestId));
    }
    // The factory carries the raw response body in `details.raw` (it only omits
    // it when the backend supplied an `error.code`). Re-derive the message and
    // lift any envelope discriminator code wsr-admin's way.
    return apiErrorFromBody(err.status, err.details.raw, err.requestId, err.details.message);
  }
  // An ApiError already in wsr-admin's shape — pass through untouched, else wrap.
  if (err instanceof ApiError) return err;
  return new ApiError('UNKNOWN', 0, {});
}

/**
 * Record which request failed (`details.method` + `details.path`, query string
 * stripped — never a token or header) so a screen can show and copy it. Builds a
 * new `ApiError` with merged details (the fields are `readonly`), preserving
 * `code`, `status` and `requestId`.
 */
function withRequestContext(err: ApiError, method: string, path: string): ApiError {
  const q = path.indexOf('?');
  const details = { ...err.details, method, path: q === -1 ? path : path.slice(0, q) };
  return new ApiError(err.code, err.status, initFrom(err.requestId, details));
}

/**
 * Build an `ApiError` from a non-2xx response body. wsr-api / saas-auth error
 * bodies may carry `{ message }`, `{ error: { message } }`, or a non-JSON
 * string; we map the status to a code and preserve any message for diagnostics.
 *
 * wsr-api's error envelope is `{ code, message, status }`
 * (HANDOFF-wsr-api-platform-foundation §4.2). The fine-grained backend code is
 * lifted from a top-level string **`code`** (or, as a fallback, a top-level
 * string **`error`**) into `details.code`, so a feature module can branch on it
 * without widening the transport `ErrorCode` union. (A `{ error: { message } }`
 * OBJECT is not a discriminator; only its nested message is read.) Only present
 * keys are assigned (never `undefined`), satisfying `exactOptionalPropertyTypes`.
 */
function apiErrorFromBody(
  status: number,
  body: unknown,
  requestId: string | undefined,
  fallbackMessage?: string,
): ApiError {
  const code: ErrorCode = statusToCode(status);
  let message: string | undefined = fallbackMessage;
  let envelopeCode: string | undefined;
  let field: string | undefined;
  let serverDetails: object | undefined;
  if (body && typeof body === 'object') {
    const b = body as {
      code?: unknown;
      message?: unknown;
      error?: unknown;
      field?: unknown;
      details?: unknown;
    };
    if (typeof b.message === 'string') message = b.message;
    else if (
      b.error &&
      typeof b.error === 'object' &&
      typeof (b.error as { message?: unknown }).message === 'string'
    ) {
      message = (b.error as { message: string }).message;
    }
    // Discriminator: prefer the standard envelope `code`; fall back to a
    // top-level string `error`. An `error` OBJECT carries only a message.
    if (typeof b.code === 'string') envelopeCode = b.code;
    else if (typeof b.error === 'string') envelopeCode = b.error;
    // Validation 400s may include `field` (membership-config card key).
    if (typeof b.field === 'string') field = b.field;
    // Keep the envelope's `details` object verbatim (e.g. the staging-only
    // `diagnostic` block) so a screen can show and copy it.
    if (b.details && typeof b.details === 'object') serverDetails = b.details;
  } else if (typeof body === 'string' && body.length > 0) {
    message = body;
  }
  let details: Record<string, unknown> | undefined;
  if (
    message !== undefined ||
    envelopeCode !== undefined ||
    field !== undefined ||
    serverDetails !== undefined
  ) {
    details = {};
    if (message !== undefined) details['message'] = message;
    if (envelopeCode !== undefined) details['code'] = envelopeCode;
    if (field !== undefined) details['field'] = field;
    if (serverDetails !== undefined) details['server'] = serverDetails;
  }
  return new ApiError(code, status, initFrom(requestId, details));
}

/**
 * Build an `ApiErrorInit` without setting `undefined` properties, satisfying
 * `exactOptionalPropertyTypes`. Only present keys are assigned.
 */
function initFrom(
  requestId: string | undefined,
  details?: Record<string, unknown>,
): { requestId?: string; details?: Record<string, unknown> } {
  const init: { requestId?: string; details?: Record<string, unknown> } = {};
  if (requestId !== undefined) init.requestId = requestId;
  if (details !== undefined) init.details = details;
  return init;
}
